import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaInstagram, FaTwitter, FaReddit, FaEnvelope, FaPhone, FaHeart } from 'react-icons/fa';
import { useApp } from '../../contexts/AppContext';
import { useTranslation } from '../../hooks/useTranslation';
import config from '../../config/app.config';

const FooterContainer = styled.footer`
  background-color: ${props => props.theme === 'dark' ? '#0f172a' : '#000'};
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  padding: 60px 0 20px;
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #e63946;
  }
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  object-fit: contain;
  margin-bottom: 1rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 50px;
  }
`;

const CompanyDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#ccc'};
  margin-bottom: 1.5rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(230, 57, 70, 0.1);
  color: #e63946;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    background: #e63946;
    color: white;
    transform: translateY(-2px);
  }
`;

const NavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NavLink = styled(Link)`
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#ccc'};
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
  display: block;
  
  &:hover {
    color: #e63946;
  }
`;

const ExternalNavLink = styled.a`
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#ccc'};
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
  display: block;
  
  &:hover {
    color: #e63946;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#ccc'};
  
  @media (max-width: 768px) {
    justify-content: center;
  }
  
  svg {
    color: #e63946;
    flex-shrink: 0;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#555'};
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: ${props => props.theme === 'dark' ? '#9ca3af' : '#999'};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.div`
  .heart {
    color: #e63946;
    margin: 0 0.25rem;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme === 'dark' ? '#9ca3af' : '#999'};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #e63946;
  }
`;

const Footer = () => {
  const { theme } = useApp();
  const { tFooter } = useTranslation();
  const currentYear = new Date().getFullYear();

  const socialMediaLinks = [
    { href: 'https://instagram.com/yasyllc', icon: FaInstagram, label: tFooter('socialLinks.instagram') },
    { href: 'https://twitter.com/yasyllc', icon: FaTwitter, label: tFooter('socialLinks.twitter') },
    { href: 'https://reddit.com/r/yasyllc', icon: FaReddit, label: tFooter('socialLinks.reddit') }
  ];

  const navigationLinks = [
    { to: config.routes.public.home, label: tFooter('navigation.home') },
    { to: config.routes.public.features, label: tFooter('navigation.features') },
    { to: config.routes.public.monitor, label: tFooter('navigation.monitor') },
    { to: config.routes.public.contact, label: tFooter('navigation.contact') },
    { href: 'https://blog.punchia.online', label: tFooter('navigation.blog'), external: true },
    { to: config.routes.public.donations, label: tFooter('navigation.donations') }
  ];

  const legalLinks = [
    { to: config.routes.public.privacy, label: tFooter('privacy') },
    { to: config.routes.public.cookies, label: tFooter('cookies') },
    { href: '/sitemap.xml', label: tFooter('sitemap'), external: true }
  ];

  return (
    <FooterContainer theme={theme}>
      <FooterContent>
        <FooterGrid>
          {/* Sección de la empresa */}
          <FooterSection>
            <Link to={config.routes.public.home}>
              <Logo src="/Logo.jpg" alt={tFooter('logoAlt')} />
            </Link>
            <CompanyDescription theme={theme}>
              {tFooter('companyDescription')}
            </CompanyDescription>
            <SocialLinks>
              {socialMediaLinks.map((social, index) => (
                <SocialLink
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </SocialLink>
              ))}
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <h3>{tFooter('sectionNavigation')}</h3>
            <NavLinks>
              {navigationLinks.map((link, index) =>
                link.external ? (
                  <ExternalNavLink
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    theme={theme}
                  >
                    {link.label}
                  </ExternalNavLink>
                ) : (
                  <NavLink key={index} to={link.to} theme={theme}>
                    {link.label}
                  </NavLink>
                )
              )}
            </NavLinks>
          </FooterSection>

          <FooterSection>
            <h3>{tFooter('sectionLegal')}</h3>
            <NavLinks>
              {legalLinks.map((link, index) => 
                link.external ? (
                  <ExternalNavLink
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    theme={theme}
                  >
                    {link.label}
                  </ExternalNavLink>
                ) : (
                  <NavLink key={index} to={link.to} theme={theme}>
                    {link.label}
                  </NavLink>
                )
              )}
            </NavLinks>
          </FooterSection>

          <FooterSection>
            <h3>{tFooter('sectionContact')}</h3>
            <ContactInfo>
              <ContactItem theme={theme}>
                <FaEnvelope size={16} aria-hidden />
                <span>{tFooter('contact.email')}</span>
              </ContactItem>
              <ContactItem theme={theme}>
                <FaPhone size={16} aria-hidden />
                <span>{tFooter('contact.phone')}</span>
              </ContactItem>
            </ContactInfo>
          </FooterSection>
        </FooterGrid>

        <FooterBottom theme={theme}>
          <Copyright>
            &copy; {currentYear} {config.app.name}. {tFooter('copyright')}
            <br />
            {tFooter('madeWith')} <FaHeart className="heart" size={12} aria-hidden /> {tFooter('madeBy')}
          </Copyright>
          <FooterLinks>
            <FooterLink to={config.routes.public.privacy} theme={theme}>{tFooter('privacy')}</FooterLink>
            <FooterLink to={config.routes.public.cookies} theme={theme}>{tFooter('cookies')}</FooterLink>
            <span>v{config.app.version}</span>
          </FooterLinks>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 